/**
 * @file Renders the user group tags for a table row.
 */
import React from "react";
import { useOutletContext } from "react-router-dom";
import { Space, Tag } from "antd";

import { ROLES_MAP } from "@components/Domain";

const TAGS_SPACE_SIZE = 0;

type UserGroup = {
  id: string;
  name: string;
  role: string;
};

type OutletContext = {
  getUserGroup: (id: string) => UserGroup | undefined;
};

type Props = {
  userGroupIds: string[];
};

type RolesMap = Record<string, { tagColor: string } | undefined>;

/**
 * Resolves the tag color of a user group's role, or undefined when the role is not known.
 */
const getRoleTagColor = (role: string): string | undefined => {
  const roleConfig = (ROLES_MAP as RolesMap)[role];

  if (!roleConfig) {
    return undefined;
  }

  return roleConfig.tagColor;
};

/**
 * Renders a tag for each of the user's named groups, colored by the group's role, or nothing when there are none.
 */
const UserGroupsColumn = ({ userGroupIds }: Props): JSX.Element | null => {
  const { getUserGroup } = useOutletContext<OutletContext>();

  const hasUserGroupIds = userGroupIds.length > 0;

  if (!hasUserGroupIds) {
    return null;
  }

  const activeUserGroups = userGroupIds
    .map((id) => getUserGroup(id))
    .filter((userGroup): userGroup is UserGroup => !!userGroup);

  return (
    <Space size={TAGS_SPACE_SIZE}>
      {
        activeUserGroups.map(({ id, name, role }) => (
          <Tag key={id} color={getRoleTagColor(role)}>{name}</Tag>
        ))
      }
    </Space>
  );
};

export default UserGroupsColumn;
